/**
 * Core PageHeader Component
 *
 * Standardized page header with title, subtitle, breadcrumbs slot,
 * action buttons area, and back navigation. Use on every page top.
 *
 * @example
 *   <PageHeader
 *     title="Data Sources"
 *     subtitle="Manage your data connections"
 *     actions={<Button leftIcon={<PlusCircle />}>Create</Button>}
 *   />
 */
import React from 'react';
import { Typography, Box, IconButton, Divider } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  /** Page title */
  title: React.ReactNode;
  /** Subtitle / description */
  subtitle?: string;
  /** Icon before title */
  icon?: React.ReactNode;
  /** Back button click handler */
  onBack?: () => void;
  /** Breadcrumbs slot (rendered above title) */
  breadcrumbs?: React.ReactNode;
  /** Right-side action buttons */
  actions?: React.ReactNode;
  /** Extra content below title (e.g. tabs, search bar) */
  extra?: React.ReactNode;
  /** Bottom divider */
  divider?: boolean;
  /** Compact padding */
  compact?: boolean;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  onBack,
  breadcrumbs,
  actions,
  extra,
  divider = false,
  compact = false,
  className = '',
}) => (
  <Box className={cn(compact ? 'mb-3' : 'mb-5', className)}>
    {breadcrumbs && <Box sx={{ mb: 1.5 }}>{breadcrumbs}</Box>}

    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ mr: 0.5 }}>
            <ArrowLeft className="h-5 w-5" />
          </IconButton>
        )}
        {icon && (
          <div className="p-2 rounded-xl bg-gradient-to-br from-[var(--primary-color)] to-blue-600 text-white shadow-md shrink-0">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          {typeof title === 'string' ? (
            <Typography variant={compact ? 'h6' : 'h5'} fontWeight={800} color="text.primary" noWrap>
              {title}
            </Typography>
          ) : title}
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </div>
      </Box>

      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>

    {extra && <Box sx={{ mt: 2 }}>{extra}</Box>}
    {divider && <Divider sx={{ mt: 2 }} />}
  </Box>
);
export default PageHeader;
